import { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_ACCESS_OPTIONS = [
  { key: "inventory", label: "Inventory", helper: "Stock, pricing, and expirations" },
  { key: "invoices", label: "Invoices", helper: "Issue and settle invoices" },
  { key: "suppliers", label: "Suppliers", helper: "Create, edit, and archive suppliers" },
  { key: "customers", label: "Customers", helper: "Account creation and CRM data" },
  { key: "reports", label: "Reports", helper: "Download and share reports" },
  { key: "settings", label: "Settings", helper: "Org preferences and billing" },
];

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  eligibility: [],
  role: "employee",
};

const normalizeRole = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "super admin" || normalized === "super_admin" || normalized === "admin") {
    return "admin";
  }
  return "employee";
};

const mapUserToForm = (user, options) => {
  const validKeys = new Set(options.map((opt) => opt.key));
  const eligibility = Array.isArray(user?.eligibility)
    ? user.eligibility.filter((key) => validKeys.has(key))
    : [];

  return {
    ...INITIAL_FORM,
    name: user?.name || user?.fullName || "",
    email: user?.email || "",
    phone: user?.phone || user?.contact?.phone || "",
    role: normalizeRole(user?.role || "employee"),
    eligibility,
  };
};

function UserDrawer({
  open,
  onClose,
  onSubmit,
  saving = false,
  error = "",
  accessOptions = DEFAULT_ACCESS_OPTIONS,
  user = null,
  mode = "create",
  withOverlay = false,
}) {
  const drawerRef = useRef(null);
  const options = useMemo(
    () => (accessOptions && accessOptions.length ? accessOptions : DEFAULT_ACCESS_OPTIONS),
    [accessOptions]
  );
  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    eligibility: options.slice(0, 3).map((opt) => opt.key),
  }));
  const [localError, setLocalError] = useState("");
  const isEditMode = mode === "edit" || Boolean(user?.id);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (!drawerRef.current) return;
      if (drawerRef.current.contains(event.target)) return;
      onClose?.();
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      const nextForm = user ? mapUserToForm(user, options) : { ...INITIAL_FORM, eligibility: options.map((opt) => opt.key) };
      setForm(nextForm);
      setLocalError("");
    }
  }, [open, options, user, mode]);

  const eligibilitySet = useMemo(() => new Set(form.eligibility), [form.eligibility]);
  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? "";
    setForm((prev) => ({ ...prev, [field]: value }));
    setLocalError("");
  };

  const handleToggleAccess = (key) => () => {
    setForm((prev) => {
      const exists = prev.eligibility.includes(key);
      const eligibility = exists ? prev.eligibility.filter((entry) => entry !== key) : [...prev.eligibility, key];
      return { ...prev, eligibility };
    });
    setLocalError("");
  };

  const selectAll = () => {
    setForm((prev) => ({ ...prev, eligibility: options.map((opt) => opt.key) }));
  };

  const clearAll = () => {
    setForm((prev) => ({ ...prev, eligibility: [] }));
  };

  const validateForm = () => {
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name) {
      setLocalError("Name is required.");
      return false;
    }
    if (!email) {
      setLocalError("Email is required.");
      return false;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setLocalError("Email looks invalid.");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim(),
      eligibility: Array.from(new Set(form.eligibility)),
      role: (form.role || "employee").toLowerCase(),
    };
    onSubmit?.(payload);
  };

  if (!open) return null;

  const content = (
    <aside className="create-drawer" ref={drawerRef} aria-label="Create user panel">
      {localError || error ? (
        <div className="inline-alert error">
          <span>{localError || error}</span>
        </div>
      ) : null}

      <div className="drawer-section">
        <h3>{isEditMode ? "Edit user" : "User details"}</h3>
        <div className="drawer-grid">
          <label className="field">
            <span className="field-label">Full name</span>
            <input
              type="text"
              value={form.name}
              onChange={handleChange("name")}
            />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input
              type="email"
              placeholder="name@company.com"
              value={form.email}
              onChange={handleChange("email")}
              readOnly={isEditMode}
              aria-readonly={isEditMode}
            />
          </label>
          <label className="field">
            <span className="field-label">Phone</span>
            <input
              type="tel"
              placeholder="+965 5555 5555"
              value={form.phone}
              onChange={handleChange("phone")}
            />
          </label>
          <label className="field">
            <span className="field-label">Role</span>
            <select value={form.role} onChange={handleChange("role")}>
              <option value="admin">Admin</option>
              <option value="employee">Employee</option>
            </select>
          </label>
        </div>
        <div className="inline-alert info compact">
          <span>
            {isEditMode
              ? "Edits save immediately—no new email is sent."
              : "On save, they receive an email with their login, a generated password, and a link to the admin panel (admin.highwindow-kwt.com)."}
          </span>
        </div>
      </div>

      <div className="drawer-section">
        <div className="drawer-head eligibility-head">
          <div className="drawer-title-group">
            <div className="field-label">Eligibility</div>
            <div className="muted extra-small">Pick the tabs this user can open.</div>
          </div>
          <div className="status-row eligibility-actions">
            <button type="button" className="pill status active" onClick={selectAll}>
              Give all access
            </button>
            <button type="button" className="pill status" onClick={clearAll}>
              Clear
            </button>
          </div>
        </div>
        <div className="eligibility-grid">
          {options.map((option) => (
            <label key={option.key} className={`eligibility-card ${eligibilitySet.has(option.key) ? "active" : ""}`}>
              <input
                type="checkbox"
                checked={eligibilitySet.has(option.key)}
                onChange={handleToggleAccess(option.key)}
                aria-label={option.label}
              />
              <div className="eligibility-copy">
                <div className="eligibility-title">{option.label}</div>
                {option.helper ? <div className="muted extra-small">{option.helper}</div> : null}
              </div>
            </label>
          ))}
        </div>
      </div>

      <footer className="drawer-footer">
        <button className="primary-btn wide" type="button" onClick={handleSubmit} disabled={saving}>
          {saving ? (isEditMode ? "Saving changes..." : "Saving user...") : isEditMode ? "Save changes" : "Save user"}
        </button>
        <button className="ghost-btn subtle wide" type="button" onClick={onClose} disabled={saving}>
          Cancel
        </button>
      </footer>
    </aside>
  );

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  if (!withOverlay) return content;

  return (
    <div className="modal-overlay solo-overlay" role="dialog" aria-modal="true" onClick={handleOverlayClick}>
      <div className="create-layer solo-drawer">{content}</div>
    </div>
  );
}

export default UserDrawer;
